/**
 * React Query hooks for the Accounts Payable module.
 *
 * These mirror the data-layer convention used elsewhere in the app: a
 * query-key factory, a `queryOptions`
 * factory that takes the axios instance, and a thin `useQuery`/`useMutation`
 * wrapper that supplies it via `useApi()`.
 *
 * Endpoints are served by the Rails `Businesses::Ap::*` controllers under
 * `/businesses/:businessId/ap`. Response bodies map onto `types.ts` with no
 * transform.
 */
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect, useRef } from "react";
import { toast } from "@/core/components/ui/toast";
import { useApi } from "@/core/api";
import useAuth from "@/core/auth/use-auth";
import { dayjs } from "@/core/utils/date";
import type {
  ApDayRange,
  ApAgentAction,
  ApAgentActionComment,
  ApAgentRun,
  ApAutomatedRunItem,
  ApBulkUploadResponse,
  ApDocumentType,
  ApEmailResponse,
  ApInvoice,
  ApInvoiceStatus,
  ApReviewState,
  ApJobSearchResult,
  ApPage,
  ApPricebookItemOption,
  ApPurchaseDocument,
  ApPurchaseOrderOption,
  ApReceipt,
  ApReviewQueueItem,
  ApUploadInvoiceResponse,
  ApVendor,
  ApVendorUpdate,
  ApVendorListEdit,
} from "./types";
import {
  enrichApEmail,
  JOB_SEARCH_LIMIT,
  MIN_JOB_SEARCH_LENGTH,
  MIN_PO_SEARCH_LENGTH,
  MIN_PRICEBOOK_SEARCH_LENGTH,
} from "./utils";

/**
 * Freshness horizon, kept relatively short: the backend moves certain states
 * on its own (parsing, applying, etc.), which no mutations here invalidate.
 * Serves as both the staleness limit gating refetch triggers (tab remount,
 * window refocus) and the list queries' poll cadence.
 */
const AP_STALE_TIME = 60 * 1000;

/**
 * While an invoice upload is this recent, the invoice-bearing lists poll past
 * the page-count cutoff in `isDeepList`: the uploader was just promised new
 * invoices "in a minute or two" and may be waiting to see them.
 */
const INVOICE_UPLOAD_BOOST_WINDOW_MS = 2 * 60 * 1000;
let lastInvoiceUploadAt: ReturnType<typeof dayjs> | null = null;

const isDeepList = (query: { state: { data?: { pages: unknown[] } } }) =>
  (query.state.data?.pages.length ?? 0) > 2;

const invoiceUploadIsRecent = () =>
  lastInvoiceUploadAt !== null &&
  dayjs().diff(lastInvoiceUploadAt) < INVOICE_UPLOAD_BOOST_WINDOW_MS;

/**
 * Background poll for the AP list queries, skipped for deep lists:
 * refetching an infinite query refreshes every loaded page, so polling one
 * multiplies requests.
 */
const apListRefetchInterval = (query: {
  state: { data?: { pages: unknown[] } };
}) => (isDeepList(query) ? false : AP_STALE_TIME);

/**
 * Poll for the lists where an uploaded invoice can land (the same ones the
 * upload mutation invalidates): the cutoff is bypassed while an upload is
 * recent.
 */
const apInvoiceListRefetchInterval = (query: {
  state: { data?: { pages: unknown[] } };
}) => (isDeepList(query) && !invoiceUploadIsRecent() ? false : AP_STALE_TIME);

/* ----------------------------------------------------------------- keys */
export const invoicesKey = (businessId?: string | null) =>
  ["ap-invoices", businessId] as const;
export const receiptsKey = (businessId?: string | null) =>
  ["ap-receipts", businessId] as const;

/**
 * Per-kind wiring for the endpoints every purchase document has. Invoices and
 * receipts are separate resources on the api; this is the one place that maps
 * a kind to its route, list cache and agent-runs filter.
 */
export const AP_DOCUMENT_ENDPOINTS = {
  invoice: { path: "invoices", listKey: invoicesKey, runsParam: "invoice_id" },
  receipt: { path: "receipts", listKey: receiptsKey, runsParam: "receipt_id" },
} as const satisfies Record<
  ApDocumentType,
  {
    path: string;
    listKey: (businessId?: string | null) => readonly unknown[];
    runsParam: string;
  }
>;
export const agentRunsKey = (businessId?: string | null) =>
  ["ap-agent-runs", businessId] as const;
export const emailsKey = (businessId?: string | null) =>
  ["ap-emails", businessId] as const;
export const vendorsKey = (businessId?: string | null) =>
  ["ap-vendors", businessId] as const;
export const reviewQueueKey = (businessId?: string | null) =>
  ["ap-review-queue", businessId] as const;
export const automatedInvoicesKey = (businessId?: string | null) =>
  ["ap-automated-invoices", businessId] as const;
export const invoiceDocumentKey = (documentSrc?: string | null) =>
  ["ap-invoice-document", documentSrc] as const;
export const jobSearchKey = (
  businessId?: string | null,
  query?: string | null,
  limit?: number,
) => ["ap-job-search", businessId, query ?? "", limit ?? null] as const;

export const pricebookSearchKey = (
  businessId?: string | null,
  query?: string | null,
) => ["ap-pricebook-search", businessId, query ?? ""] as const;

/** Every PO search for one document — the prefix a pick invalidates. */
export const documentPurchaseOrdersKey = (
  businessId?: string | null,
  documentId?: string | null,
) => ["ap-po-search", businessId, documentId] as const;
export const purchaseOrderSearchKey = (
  businessId?: string | null,
  documentId?: string | null,
  query?: string | null,
) =>
  [...documentPurchaseOrdersKey(businessId, documentId), query ?? ""] as const;

// By-id keys nest under the matching list key so the mutations' existing
// prefix invalidations cover the by-id caches too.
export const invoiceKey = (
  businessId?: string | null,
  invoiceId?: string | null,
) => [...invoicesKey(businessId), "detail", invoiceId] as const;
export const emailKey = (businessId?: string | null, emailId?: string | null) =>
  [...emailsKey(businessId), "detail", emailId] as const;
export const vendorKey = (
  businessId?: string | null,
  vendorId?: string | null,
) => [...vendorsKey(businessId), "detail", vendorId] as const;
export const invoiceAgentRunsKey = (
  businessId?: string | null,
  invoiceId?: string | null,
) => [...agentRunsKey(businessId), invoiceId] as const;
export const receiptKey = (
  businessId?: string | null,
  receiptId?: string | null,
) => [...receiptsKey(businessId), "detail", receiptId] as const;
/** Document ids are UUIDs, so one runs key per id never collides across kinds. */
export const documentAgentRunsKey = invoiceAgentRunsKey;
const documentKey = (
  documentType: ApDocumentType,
  businessId?: string | null,
  documentId?: string | null,
) =>
  documentType === "receipt"
    ? receiptKey(businessId, documentId)
    : invoiceKey(businessId, documentId);

/* ----------------------------------------------------------------- queries */

/** Server-side list filters for `useGetInvoices` (invoices#index params). */
export interface ApInvoiceListFilters {
  /** Any of these statuses; empty or omitted means all. */
  statuses?: ApInvoiceStatus[];
  /** Substring match on vendor name OR invoice number (ILIKE server-side). */
  search?: string;
  /**
   * Added (`created_at`) between these days (YYYY-MM-DD, both inclusive). The server
   * reads them in the business's time zone.
   */
  added?: ApDayRange | null;
  /** Became reviewed between these days, read the same way. Implies reviewed. */
  reviewed?: ApDayRange | null;
  /** Any of these review states; empty or omitted means all. */
  reviewStates?: ApReviewState[];
}

export type { ApDayRange } from "./types";

const dayParams = (prefix: "created" | "reviewed", range: ApDayRange | null) =>
  range
    ? {
        [`filter[${prefix}_from]`]: range.from,
        [`filter[${prefix}_to]`]: range.to,
      }
    : {};

/**
 * Invoices, offset-paginated with server-side filtering (server order:
 * newest `created_at` first).
 */
export const useGetInvoices = (
  businessId?: string | null,
  filters: ApInvoiceListFilters = {},
) => {
  const api = useApi();
  // Normalized so equivalent filters always hash to the same query key.
  const normalized = {
    statuses: filters.statuses?.length ? [...filters.statuses].sort() : null,
    search: filters.search?.trim() || null,
    added: filters.added ?? null,
    reviewed: filters.reviewed ?? null,
    reviewStates: filters.reviewStates?.length
      ? [...filters.reviewStates].sort()
      : null,
  };
  return useInfiniteQuery({
    queryKey: [...invoicesKey(businessId), "list", normalized] as const,
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApInvoice>>(`/businesses/${businessId}/ap/invoices`, {
          params: {
            page: pageParam,
            ...(normalized.statuses && {
              "filter[status]": normalized.statuses,
            }),
            ...(normalized.search && { q: normalized.search }),
            ...dayParams("created", normalized.added),
            ...dayParams("reviewed", normalized.reviewed),
            ...(normalized.reviewStates && {
              "filter[review_state]": normalized.reviewStates,
            }),
          },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    // Keep the previous rows visible while a filter/search change loads its
    // first page — but never across a business switch (key index 1).
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === businessId ? previousData : undefined,
    staleTime: AP_STALE_TIME,
    refetchInterval: apInvoiceListRefetchInterval,
  });
};

/** Returns the poll interval (ms) or false, given the latest runs. */
type RunsRefetchInterval =
  | number
  | false
  | ((query: { state: { data?: ApAgentRun[] } }) => number | false);

export const documentAgentRunsQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string | null | undefined,
) =>
  queryOptions({
    queryKey: documentAgentRunsKey(businessId, documentId),
    queryFn: () =>
      api
        .get<ApAgentRun[]>(`/businesses/${businessId}/ap/agent_runs`, {
          params: {
            [AP_DOCUMENT_ENDPOINTS[documentType].runsParam]: documentId,
          },
        })
        .then((response) => response.data),
    enabled: !!businessId && !!documentId,
    staleTime: AP_STALE_TIME,
  });

export const invoiceAgentRunsQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  invoiceId: string | null | undefined,
) => documentAgentRunsQueryOptions(api, businessId, "invoice", invoiceId);

/**
 * Everything about a document's runs that its `review_state` is derived from
 * on the API (the newest run's status, and each action's status and
 * rollback), as one comparable string. Null until the runs have loaded.
 */
export const agentRunsReviewSignature = (
  runs: ApAgentRun[] | undefined,
): string | null =>
  runs
    ? runs
        .map((run) =>
          [
            run.id,
            run.status,
            ...run.actions.map(
              (action) =>
                `${action.id}:${action.status}:${action.rollback_status ?? ""}`,
            ),
          ].join("|"),
        )
        .join(";")
    : null;

/**
 * A document's agent runs, newest first (per the backend ordering).
 *
 * The document's review state is derived from these runs on the API but
 * arrives with the document, which is fetched separately. When a poll shows
 * the runs moved (a run finished, an action applied or was rolled back), the
 * document is refetched too, so an open panel's Review field doesn't lag.
 */
export const useGetDocumentAgentRuns = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string | null | undefined,
  // Optional poll — e.g. while an approved action is still applying to
  // ServiceTitan, so the card flips to applied/failed on its own.
  refetchInterval?: RunsRefetchInterval,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery({
    ...documentAgentRunsQueryOptions(api, businessId, documentType, documentId),
    refetchInterval,
  });

  const signature = agentRunsReviewSignature(query.data);
  const seenSignature = useRef<string | null>(null);
  useEffect(() => {
    if (signature === null) return;
    const previous = seenSignature.current;
    seenSignature.current = signature;
    if (previous === null || previous === signature) return;

    void queryClient.invalidateQueries({
      queryKey: documentKey(documentType, businessId, documentId),
      exact: true,
    });
  }, [signature, queryClient, documentType, businessId, documentId]);

  return query;
};

/** An invoice's agent runs, newest first (per the backend ordering). */
export const useGetInvoiceAgentRuns = (
  businessId?: string | null,
  invoiceId?: string | null,
  refetchInterval?: RunsRefetchInterval,
) => useGetDocumentAgentRuns(businessId, "invoice", invoiceId, refetchInterval);

/** Server-side list filters for `useGetReceipts` (receipts#index params). */
export type ApReceiptListFilters = ApInvoiceListFilters;

/**
 * Receipts, offset-paginated with server-side filtering (server order:
 * newest `created_at` first). `search` matches vendor or receipt number.
 */
export const useGetReceipts = (
  businessId?: string | null,
  filters: ApReceiptListFilters = {},
) => {
  const api = useApi();
  const normalized = {
    statuses: filters.statuses?.length ? [...filters.statuses].sort() : null,
    search: filters.search?.trim() || null,
    added: filters.added ?? null,
    reviewed: filters.reviewed ?? null,
    reviewStates: filters.reviewStates?.length
      ? [...filters.reviewStates].sort()
      : null,
  };
  return useInfiniteQuery({
    queryKey: [...receiptsKey(businessId), "list", normalized] as const,
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApReceipt>>(`/businesses/${businessId}/ap/receipts`, {
          params: {
            page: pageParam,
            ...(normalized.statuses && {
              "filter[status]": normalized.statuses,
            }),
            ...(normalized.search && { q: normalized.search }),
            ...dayParams("created", normalized.added),
            ...dayParams("reviewed", normalized.reviewed),
            ...(normalized.reviewStates && {
              "filter[review_state]": normalized.reviewStates,
            }),
          },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === businessId ? previousData : undefined,
    staleTime: AP_STALE_TIME,
    refetchInterval: apInvoiceListRefetchInterval,
  });
};

/** A single receipt by id (same payload shape as a list item). */
export const useGetReceipt = (
  businessId?: string | null,
  receiptId?: string | null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: receiptKey(businessId, receiptId),
    queryFn: () =>
      api
        .get<ApReceipt>(`/businesses/${businessId}/ap/receipts/${receiptId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!receiptId,
    staleTime: AP_STALE_TIME,
  });
};

/**
 * An invoice or receipt by id, under the same cache key as its own by-id
 * query — for a caller that only knows the kind at runtime, such as a review
 * row reaching for the document's lines. `enabled` defers the fetch.
 */
export const useGetPurchaseDocument = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const api = useApi();
  return useQuery({
    queryKey: documentKey(documentType, businessId, documentId),
    queryFn: () =>
      api
        .get<
          ApInvoice | ApReceipt
        >(`/businesses/${businessId}/ap/${AP_DOCUMENT_ENDPOINTS[documentType].path}/${documentId}`)
        .then((response) => response.data),
    enabled: enabled && !!businessId && !!documentId,
    staleTime: AP_STALE_TIME,
  });
};

/** Emails, offset-paginated (server order: newest first). */
export const useGetEmails = (businessId?: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: emailsKey(businessId),
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApEmailResponse>>(`/businesses/${businessId}/ap/emails`, {
          params: { page: pageParam },
        })
        .then((response) => ({
          ...response.data,
          items: response.data.items.map(enrichApEmail),
        })),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apListRefetchInterval,
  });
};

/** Vendors, offset-paginated (server order: by name). */
export const useGetVendors = (businessId?: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: vendorsKey(businessId),
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApVendor>>(`/businesses/${businessId}/ap/vendors`, {
          params: { page: pageParam },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apListRefetchInterval,
  });
};

/* ------------------------------------------------------------- by-id queries */

export const invoiceQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  invoiceId: string | null | undefined,
) =>
  queryOptions({
    queryKey: invoiceKey(businessId, invoiceId),
    queryFn: () =>
      api
        .get<ApInvoice>(`/businesses/${businessId}/ap/invoices/${invoiceId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!invoiceId,
    staleTime: AP_STALE_TIME,
  });

/**
 * A single invoice by id — makes deep links work regardless of what the
 * paginated list caches hold. Same payload shape as a list item (including
 * the nested `attachment`).
 */
export const useGetInvoice = (
  businessId?: string | null,
  invoiceId?: string | null,
) => {
  const api = useApi();
  return useQuery(invoiceQueryOptions(api, businessId, invoiceId));
};

/**
 * Several invoices by id (one cached query each — an email's attachments
 * usually reference only 1–2). Returns the ones that have loaded so far,
 * plus combined `isLoading`/`isError` so consumers can tell "still fetching"
 * apart from "nothing matched" and can flag partial failures.
 */
export const useGetInvoicesByIds = (
  businessId: string | null | undefined,
  invoiceIds: string[],
) => {
  const api = useApi();
  return useQueries({
    queries: invoiceIds.map((invoiceId) =>
      invoiceQueryOptions(api, businessId, invoiceId),
    ),
    combine: (results) => ({
      invoices: results.flatMap((result) => (result.data ? [result.data] : [])),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    }),
  });
};

export const emailQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  emailId: string | null | undefined,
) =>
  queryOptions({
    queryKey: emailKey(businessId, emailId),
    queryFn: () =>
      api
        .get<ApEmailResponse>(`/businesses/${businessId}/ap/emails/${emailId}`)
        .then((response) => enrichApEmail(response.data)),
    enabled: !!businessId && !!emailId,
    staleTime: AP_STALE_TIME,
  });

export const useGetEmail = (
  businessId?: string | null,
  emailId?: string | null,
) => {
  const api = useApi();
  return useQuery(emailQueryOptions(api, businessId, emailId));
};

export const vendorQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  vendorId: string | null | undefined,
) =>
  queryOptions({
    queryKey: vendorKey(businessId, vendorId),
    queryFn: () =>
      api
        .get<ApVendor>(`/businesses/${businessId}/ap/vendors/${vendorId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!vendorId,
    staleTime: AP_STALE_TIME,
  });

export const useGetVendor = (
  businessId?: string | null,
  vendorId?: string | null,
) => {
  const api = useApi();
  return useQuery(vendorQueryOptions(api, businessId, vendorId));
};

/* ------------------------------------------------------------- review queue */

/**
 * The Review queue: invoices whose last completed agent run has pending
 * actions, newest-first by `created_at` (server-ordered), each with its live
 * run nested. No total count by design — the tab's "needs review" dot derives
 * from the first page being non-empty.
 */
export const useGetReviewQueue = (
  businessId?: string | null,
  refreshOnMount = false,
) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: reviewQueueKey(businessId),
    refetchOnMount: refreshOnMount ? "always" : true,
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApReviewQueueItem>>(
          `/businesses/${businessId}/ap/invoices/review_queue`,
          {
            params: { page: pageParam },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apInvoiceListRefetchInterval,
  });
};

/**
 * The Automated tab: invoices handled by full automation, newest first, each
 * with its newest qualified run nested.
 */
export const useGetAutomatedInvoices = (businessId?: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: automatedInvoicesKey(businessId),
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApAutomatedRunItem>>(
          `/businesses/${businessId}/ap/invoices/automated`,
          {
            params: { page: pageParam },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apListRefetchInterval,
  });
};

/**
 * Fetches an AP invoice's document bytes as a Blob.
 *
 * `documentSrc` is the absolute URL resolved against the API origin (see
 * `useApInvoiceDocument`), so axios ignores the instance baseURL and hits the
 * API origin directly.
 */
export const invoiceDocumentQueryOptions = (
  api: AxiosInstance,
  documentSrc: string | null | undefined,
) =>
  queryOptions({
    queryKey: invoiceDocumentKey(documentSrc),
    queryFn: () =>
      api
        .get<Blob>(documentSrc as string, { responseType: "blob" })
        .then((response) => response.data),
    enabled: !!documentSrc,
    // Not AP_STALE_TIME: the bytes behind a given URL never change, and
    // re-downloading a PDF is far more expensive than re-reading a row.
    staleTime: 5 * 60 * 1000,
  });

/* ------------------------------------------------------- manual job matching */

/**
 * ServiceTitan job search backing the manual job-match combobox.
 * GET /businesses/:id/ap/jobs/search?q=&limit=
 *
 * The same jobs index that serves the date-range list — `q` switches it to a
 * live ServiceTitan search. The query is keyed on the trimmed text, so trailing
 * whitespace reuses the same cache entry, and it never fires below
 * `MIN_JOB_SEARCH_LENGTH` — the server would only answer with an empty list.
 */
export const apJobSearchQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  query: string,
  limit: number = JOB_SEARCH_LIMIT,
) => {
  const q = query.trim();
  return queryOptions({
    queryKey: jobSearchKey(businessId, q, limit),
    queryFn: ({ signal }) =>
      api
        .get<ApJobSearchResult[]>(`/businesses/${businessId}/ap/jobs/search`, {
          params: { q, limit },
          signal,
        })
        .then((response) => response.data),
    enabled: !!businessId && q.length >= MIN_JOB_SEARCH_LENGTH,
    staleTime: AP_STALE_TIME,
  });
};

/**
 * Jobs matching a search string. Feed it an already-debounced query — every
 * distinct value is a request.
 */
export const useSearchJobs = (
  businessId?: string | null,
  query = "",
  limit: number = JOB_SEARCH_LIMIT,
) => {
  const api = useApi();
  return useQuery({
    ...apJobSearchQueryOptions(api, businessId, query, limit),
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Pricebook items matching a search string (code, name or description, exact
 * codes first), read from the local pricebook copy rather than ServiceTitan.
 * Feed it an already-debounced query — every distinct value is a request.
 * GET /businesses/:id/ap/pricebook_items?q=
 */
export const useSearchPricebookItems = (
  businessId?: string | null,
  query = "",
) => {
  const api = useApi();
  const q = query.trim();
  return useQuery({
    queryKey: pricebookSearchKey(businessId, q),
    queryFn: ({ signal }) =>
      api
        .get<
          ApPricebookItemOption[]
        >(`/businesses/${businessId}/ap/pricebook_items`, { params: { q }, signal })
        .then((response) => response.data),
    enabled: !!businessId && q.length >= MIN_PRICEBOOK_SEARCH_LENGTH,
    staleTime: AP_STALE_TIME,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * The ServiceTitan POs a document can be attributed to. A blank query lists
 * the POs on the document's job (and the PO it already carries); otherwise a
 * PO number or job number lookup, live in ServiceTitan — so it never fires
 * below `MIN_PO_SEARCH_LENGTH`. Feed it an already-debounced query.
 * GET /businesses/:id/ap/:kind/:documentId/purchase_orders?q=
 */
export const useSearchDocumentPurchaseOrders = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string,
  query = "",
) => {
  const api = useApi();
  const q = query.trim();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useQuery({
    queryKey: purchaseOrderSearchKey(businessId, documentId, q),
    queryFn: ({ signal }) =>
      api
        .get<
          ApPurchaseOrderOption[]
        >(`/businesses/${businessId}/ap/${endpoints.path}/${documentId}/purchase_orders`, { params: q ? { q } : {}, signal })
        .then((response) => response.data),
    enabled: !!businessId && (q === "" || q.length >= MIN_PO_SEARCH_LENGTH),
    // Not AP_STALE_TIME: the list depends on the document's job and PO, which
    // a job match (by hand or by the agent) changes under the same key. The
    // api caches it briefly, keyed on both, so reopening stays cheap.
    staleTime: 0,
    placeholderData: (previousData) => previousData,
  });
};

/* ----------------------------------------------------------------- mutations */

/**
 * Manually attribute an invoice to a ServiceTitan job — the escape hatch when
 * automatic matching fails.
 * POST /businesses/:id/ap/invoices/:invoiceId/match_job
 *   Body: { st_job_external_id }
 *
 * A member action rather than an invoice update: the job is the only field a
 * client may set here, and it sits alongside `retry_matching` / `run_agent`.
 *
 * Responds with the updated invoice (the `invoices#show` shape), which lands in
 * the by-id cache so the panel flips immediately. The server re-runs the agent
 * against the new match, so the runs and the invoice-bearing lists are
 * invalidated rather than written locally.
 */
export const useMatchDocumentJob = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (vars: { documentId: string; job: ApJobSearchResult }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/match_job`,
          { st_job_external_id: vars.job.id },
        )
        .then((response) => response.data),
    onSuccess: (document, vars) => {
      queryClient.setQueryData(
        documentKey(documentType, businessId, vars.documentId),
        document,
      );
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
      // A new job drops the document's PO and changes its candidates.
      void queryClient.invalidateQueries({
        queryKey: documentPurchaseOrdersKey(businessId, vars.documentId),
      });
    },
  });
};

/**
 * Manually attribute one invoice or receipt line to a pricebook item, or to
 * none (`pricebookItemId: null`) — the fix for a wrong automatic match.
 * POST /businesses/:id/ap/:kind/:documentId/match_pricebook_item
 *   Body: { line_item_id, pricebook_item_id }
 *
 * Responds with the updated document (the `#show` shape), written to the by-id
 * cache. The server moves or retires the line's pending cost review to agree,
 * rejects the agent's open proposals (they were made against the old match)
 * and queues a fresh run, so the runs and review queue are refreshed too.
 */
export const useMatchLinePricebookItem = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (vars: {
      documentId: string;
      lineItemId: string;
      pricebookItemId: string | null;
    }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/match_pricebook_item`,
          {
            line_item_id: vars.lineItemId,
            pricebook_item_id: vars.pricebookItemId,
          },
        )
        .then((response) => response.data),
    onSuccess: (document, vars) => {
      queryClient.setQueryData(
        documentKey(documentType, businessId, vars.documentId),
        document,
      );
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      // The pick rejects the agent's open proposals and queues a new run.
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/**
 * Manually attribute an invoice or receipt to a ServiceTitan PO, or record
 * that no existing PO covers it (`stPurchaseOrderId: null`).
 * POST /businesses/:id/ap/:kind/:documentId/match_purchase_order
 *   Body: { st_purchase_order_id }
 *
 * Responds with the updated document (the `#show` shape), written to the by-id
 * cache. The PO is authoritative, so the document may move to the PO's job.
 * When the PO (or job) changed, the server rejects the agent's open proposals
 * and queues a fresh run, so the runs and review queue are refreshed too.
 */
export const useMatchDocumentPurchaseOrder = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (vars: {
      documentId: string;
      stPurchaseOrderId: number | null;
    }) =>
      api
        .post<ApPurchaseDocument>(
          `/businesses/${businessId}/ap/${endpoints.path}/${vars.documentId}/match_purchase_order`,
          { st_purchase_order_id: vars.stPurchaseOrderId },
        )
        .then((response) => response.data),
    onSuccess: (document, vars) => {
      queryClient.setQueryData(
        documentKey(documentType, businessId, vars.documentId),
        document,
      );
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
      // The candidates are keyed on the document, whose job may have moved.
      void queryClient.invalidateQueries({
        queryKey: documentPurchaseOrdersKey(businessId, vars.documentId),
      });
    },
  });
};

/** Manual job match for an invoice (see `useMatchDocumentJob`). */
export const useMatchInvoiceJob = (businessId?: string | null) =>
  useMatchDocumentJob(businessId, "invoice");

/**
 * Approve or reject an agent action.
 * POST /businesses/:id/ap/agent_actions/:actionId/approve|reject
 *
 * Approval enqueues an async apply → ServiceTitan; the action transitions to
 * `applied`/`apply_failed` server-side, so we invalidate the runs (and invoices,
 * whose status/match can change on apply) rather than writing the cache locally.
 */
export const useResolveAgentAction = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      actionId: string;
      decision: "approved" | "rejected";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload?: any;
    }) =>
      api
        .post<ApAgentAction>(
          `/businesses/${businessId}/ap/agent_actions/${vars.actionId}/${
            vars.decision === "approved" ? "approve" : "reject"
          }`,
          vars.decision === "approved" && vars.payload !== undefined
            ? { payload: vars.payload }
            : undefined,
        )
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: invoicesKey(businessId) });
      void queryClient.invalidateQueries({ queryKey: receiptsKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/**
 * Approve several agent actions at once.
 * POST /businesses/:id/ap/agent_actions/bulk_approve  ·  Body: { ids: [...] }
 *
 * The server skips any id that isn't pending_review rather than failing the
 * batch, and enqueues an apply → ServiceTitan per approved action, so we
 * invalidate the same caches as the single-action resolve.
 */
export const useBulkApproveAgentActions = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api
        .post<
          ApAgentAction[]
        >(`/businesses/${businessId}/ap/agent_actions/bulk_approve`, { ids })
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: invoicesKey(businessId) });
      void queryClient.invalidateQueries({ queryKey: receiptsKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/**
 * Persist a user's edits to an agent action's proposed payload.
 * PATCH /businesses/:id/ap/agent_actions/:actionId
 */
export const useUpdateAgentAction = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (vars: { actionId: string; payload: any }) =>
      api
        .patch<ApAgentAction>(
          `/businesses/${businessId}/ap/agent_actions/${vars.actionId}`,
          { payload: vars.payload },
        )
        .then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/** Client-side id prefix marking an optimistic comment awaiting its server id. */
const OPTIMISTIC_COMMENT_PREFIX = "optimistic-";

/** True while a comment is the client-only optimistic copy (no real id yet). */
export const isOptimisticComment = (comment: ApAgentActionComment): boolean =>
  comment.id.startsWith(OPTIMISTIC_COMMENT_PREFIX);

/** Append a comment to the matching action across the cached runs. */
const withComment = (
  runs: ApAgentRun[] | undefined,
  actionId: string,
  comment: ApAgentActionComment,
) =>
  runs?.map((run) => ({
    ...run,
    actions: run.actions.map((action) =>
      action.id === actionId
        ? { ...action, comments: [...(action.comments ?? []), comment] }
        : action,
    ),
  }));

/** Remove a comment from the matching action across the cached runs. */
const withoutComment = (
  runs: ApAgentRun[] | undefined,
  actionId: string,
  commentId: string,
) =>
  runs?.map((run) => ({
    ...run,
    actions: run.actions.map((action) =>
      action.id === actionId
        ? {
            ...action,
            comments: (action.comments ?? []).filter((c) => c.id !== commentId),
          }
        : action,
    ),
  }));

/**
 * Post a reviewer comment on an agent action.
 * POST /businesses/:id/ap/agent_action_comments
 *   Body: { agent_action_id, comment: { content } }
 *
 * Comments are embedded on each action by the runs serializer, so we
 * optimistically drop the comment onto the action across the runs caches
 * (one per invoice, all nested under `agentRunsKey`) — showing it
 * immediately — then reconcile with the server copy on settle.
 */
export const useCreateAgentActionComment = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (vars: { actionId: string; content: string }) =>
      api
        .post<ApAgentActionComment>(
          `/businesses/${businessId}/ap/agent_action_comments`,
          {
            agent_action_id: vars.actionId,
            comment: { content: vars.content },
          },
        )
        .then((response) => response.data),
    onMutate: async (vars) => {
      const key = agentRunsKey(businessId);
      // Cancel in-flight refetches so they don't clobber the optimistic write.
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ApAgentRun[]>({
        queryKey: key,
      });

      const authorName = user
        ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || null
        : null;
      const now = dayjs().toISOString();
      const optimistic: ApAgentActionComment = {
        // Prefixed so the UI can tell it apart from a persisted comment (e.g.
        // to hold off the delete affordance until the real id lands). A random
        // uuid — not the timestamp — so two rapid submits can't collide.
        id: `${OPTIMISTIC_COMMENT_PREFIX}${crypto.randomUUID()}`,
        agent_action_id: vars.actionId,
        content: vars.content,
        author: authorName,
        // The poster owns it; the delete affordance stays hidden until the real
        // id lands (guarded by the optimistic prefix in the UI) regardless.
        can_delete: true,
        created_at: now,
        updated_at: now,
      };
      queryClient.setQueriesData<ApAgentRun[]>({ queryKey: key }, (runs) =>
        withComment(runs, vars.actionId, optimistic),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
    },
  });
};

/**
 * Delete a reviewer comment. The server only lets the author delete their own
 * (403 otherwise), so surface the delete affordance accordingly.
 * DELETE /businesses/:id/ap/agent_action_comments/:commentId
 */
export const useDeleteAgentActionComment = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { actionId: string; commentId: string }) =>
      api
        .delete(
          `/businesses/${businessId}/ap/agent_action_comments/${vars.commentId}`,
        )
        .then((response) => response.data),
    onMutate: async (vars) => {
      const key = agentRunsKey(businessId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueriesData<ApAgentRun[]>({
        queryKey: key,
      });
      queryClient.setQueriesData<ApAgentRun[]>({ queryKey: key }, (runs) =>
        withoutComment(runs, vars.actionId, vars.commentId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
    },
  });
};

/**
 * Upload one or more invoice files.
 * 1 file:  POST /businesses/:id/ap/invoices  ·  multipart, `file`
 * 2+ files: POST /businesses/:id/ap/invoices/bulk_upload  ·  multipart, `files[]`
 *
 * A single file goes to the single endpoint because its failure (400) carries
 * the specific reason in the error message, where the bulk 422 only keeps an
 * aggregate count. The single response is normalized to the bulk shape so
 * consumers handle one payload.
 *
 * Files are queued for async parse → match, so the new invoices don't exist
 * yet when the upload succeeds; they surface via the lists' polling.
 */
export const useUploadInvoices = (businessId?: string | null) =>
  useUploadDocuments(businessId, "invoice");

/**
 * Upload one or more files as the given document kind. A manual upload says
 * what it is, so the api skips classification and parses it as that kind.
 */
export const useUploadDocuments = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: async (files: File[]): Promise<ApBulkUploadResponse> => {
      const [single] = files;
      if (single && files.length === 1) {
        const formData = new FormData();
        formData.append("file", single, single.name);
        const { data } = await api.post<ApUploadInvoiceResponse>(
          `/businesses/${businessId}/ap/${endpoints.path}`,
          formData,
        );
        return {
          message: data.message,
          queued: [
            { file_name: single.name, attachment_id: data.attachment_id },
          ],
          skipped: [],
        };
      }

      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file, file.name));
      return api
        .post<ApBulkUploadResponse>(
          `/businesses/${businessId}/ap/${endpoints.path}/bulk_upload`,
          formData,
        )
        .then((response) => response.data);
    },
    onSuccess: () => {
      lastInvoiceUploadAt = dayjs();
      // Besides clearing stale rows, these re-arm a deep-loaded list's
      // boosted poll: refetchInterval is only re-evaluated after a fetch.
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

type VendorUpdateVariables = {
  businessId: string | null;
  vendorId: string;
} & (
  | { changes: ApVendorUpdate; listEdit?: never }
  | { listEdit: ApVendorListEdit; changes?: never }
);

/**
 * Save vendor edits in order using Query's mutation scope. List edits read the
 * server record when their turn starts; the cache only contains confirmed data.
 * Identifiers travel with each edit so delayed Undo actions keep their target.
 */
export const useUpdateVendor = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: "ap-vendor-updates" },
    mutationFn: async ({
      businessId,
      vendorId,
      changes,
      listEdit,
    }: VendorUpdateVariables) => {
      if (listEdit) {
        const vendor = await queryClient.fetchQuery({
          ...vendorQueryOptions(api, businessId, vendorId),
          staleTime: 0,
        });
        const values = vendor[listEdit.field] ?? [];
        const next =
          listEdit.action === "remove"
            ? values.filter((value) => value !== listEdit.value)
            : Array.from(new Set([...values, listEdit.value]));
        changes = { [listEdit.field]: next };
      }
      return api
        .patch<ApVendor>(
          `/businesses/${businessId}/ap/vendors/${vendorId}`,
          changes,
        )
        .then((response) => response.data);
    },
    onSuccess: async (vendor, { businessId, vendorId }) => {
      const key = vendorKey(businessId, vendorId);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData(key, vendor);
      await queryClient.invalidateQueries({ queryKey: vendorsKey(businessId) });
    },
    onError: () => {
      toast("Couldn’t update the vendor. Please try again.");
    },
  });
};
